import { useState } from 'react';
import { callable } from '@/lib/firebase';
import { messageForAuthError } from '@/lib/authErrors';
import { strings } from '@/strings';

const setCandidateConsent = callable<
  { candidateId: string; granted: boolean },
  { candidateId: string; granted: boolean; version: string }
>('setCandidateConsent');

/**
 * The screen the whole PDPL posture rests on. It says what consent permits in
 * plain Arabic BEFORE the button, names the limit that matters most (no
 * organization sees anything until this person accepts a specific
 * opportunity), and offers withdrawal on the same screen rather than burying
 * it — a consent you cannot easily withdraw is not much of a consent.
 */
export function CandidateConsentPage({
  candidateId, granted, onChanged,
}: { candidateId: string; granted: boolean; onChanged: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function set(next: boolean) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await setCandidateConsent({ candidateId, granted: next });
      onChanged();
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-lg flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.consentTitle}</h1>
      <p className="leading-7 text-ink">{strings.consentBody}</p>
      {granted ? (
        <button type="button" disabled={busy} onClick={() => set(false)}
          className="self-start rounded-md border border-danger px-4 py-2 text-danger disabled:opacity-50">
          {strings.consentWithdraw}
        </button>
      ) : (
        <button type="button" disabled={busy} onClick={() => set(true)}
          className="self-start min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50">
          {strings.consentAgree}
        </button>
      )}
      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
